import { useMemo, useState } from "react";
import {
    Heart,
    MapPin,
    Briefcase,
    Tag,
    Shuffle,
    Users,
    Star,
    GraduationCap,
    Zap,
    ChevronRight,
    Cake,
    Clock,
    Eye,
    TrendingUp,
    Mic,
    BarChart3,
} from "lucide-react";
import {
    getInitials,
    getFullName,
    getRoleDisplay,
    getAvatarColors,
    type Contact,
} from "@/lib/data";
import { ClusterView } from "@/people/ClusterView";

// ─── Avatar ─────────────────────────────────────────────────────────────────

function ContactAvatar({
    contact,
    size,
    className,
}: {
    contact: Contact;
    size: number;
    className?: string;
}) {
    const [bg1, bg2] = getAvatarColors(contact);
    const fontSize = Math.round(size * 0.35);
    if (contact.photo) {
        return (
            <img
                src={contact.photo}
                alt=""
                className={`shrink-0 rounded-full object-cover ${className ?? ""}`}
                style={{ width: size, height: size }}
            />
        );
    }
    return (
        <div
            className={`flex shrink-0 items-center justify-center rounded-full ${className ?? ""}`}
            style={{
                width: size,
                height: size,
                background: `linear-gradient(135deg, ${bg1}, ${bg2})`,
            }}
        >
            <span className="font-semibold text-white" style={{ fontSize }}>
                {getInitials(contact)}
            </span>
        </div>
    );
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface PeopleFeedProps {
    contacts: Contact[];
    onSelectContact: (contact: Contact) => void;
    onToggleFavorite: (contactId: string) => void;
}
interface ClusterItem {
    label: string;
    count: number;
    contacts: Contact[];
}
interface ActiveCluster {
    title: string;
    contacts: Contact[];
    color: string;
    subtitle?: string;
    /** When set, show a list of clusters instead of contacts */
    clusters?: ClusterItem[];
}
interface BirthdayEntry {
    contact: Contact;
    label: string;
    daysAway: number;
    month: string;
}

// ─── Color Palette ──────────────────────────────────────────────────────────

const C = {
    gold: "#F5A623",
    coral: "#FF6B6B",
    ocean: "#4FACFE",
    violet: "#A855F7",
    emerald: "#34D399",
    pink: "#EC4899",
    teal: "#2DD4BF",
    amber: "#FBBF24",
    warmOrange: "#FB923C",
    rose: "#FB7185",
};

// Each section gets completely different gradient pairs
const G = {
    location: [
        ["#06B6D4", "#3B82F6"],
        ["#8B5CF6", "#EC4899"],
        ["#10B981", "#059669"],
        ["#F59E0B", "#EF4444"],
        ["#6366F1", "#2DD4BF"],
        ["#F472B6", "#7C3AED"],
        ["#0EA5E9", "#6366F1"],
        ["#D946EF", "#F43F5E"],
    ] as [string, string][],
    industry: [
        ["#059669", "#10B981"],
        ["#DC2626", "#F97316"],
        ["#7C3AED", "#A855F7"],
        ["#0891B2", "#06B6D4"],
        ["#D97706", "#F59E0B"],
        ["#BE185D", "#EC4899"],
    ] as [string, string][],
    role: [
        ["#4F46E5", "#7C3AED"],
        ["#0D9488", "#2DD4BF"],
        ["#B91C1C", "#EF4444"],
        ["#0369A1", "#0EA5E9"],
        ["#A16207", "#F59E0B"],
        ["#7E22CE", "#D946EF"],
        ["#047857", "#10B981"],
        ["#9333EA", "#EC4899"],
    ] as [string, string][],
    skill: [
        ["#7C3AED", "#A78BFA"],
        ["#059669", "#34D399"],
        ["#DB2777", "#F472B6"],
        ["#2563EB", "#60A5FA"],
        ["#D97706", "#FCD34D"],
        ["#0891B2", "#67E8F9"],
        ["#E11D48", "#FB7185"],
        ["#4F46E5", "#818CF8"],
    ] as [string, string][],
    company: [
        ["#1E40AF", "#3B82F6"],
        ["#7E22CE", "#A855F7"],
        ["#0F766E", "#14B8A6"],
        ["#9A3412", "#F97316"],
        ["#4338CA", "#6366F1"],
        ["#9D174D", "#EC4899"],
    ] as [string, string][],
    school: [
        ["#1E3A5F", "#2563EB"],
        ["#3B0764", "#7C3AED"],
        ["#064E3B", "#059669"],
        ["#78350F", "#D97706"],
    ] as [string, string][],
};

// ─── Main Feed ──────────────────────────────────────────────────────────────

export function PeopleFeed({
    contacts,
    onSelectContact,
    onToggleFavorite,
}: PeopleFeedProps) {
    const [activeCluster, setActiveCluster] = useState<ActiveCluster | null>(null);
    const feed = useMemo(() => buildFeedData(contacts), [contacts]);

    const open = (title: string, c: Contact[], color: string, sub?: string) =>
        setActiveCluster({ title, contacts: c, color, subtitle: sub });

    /** Open a "See All" view that shows clusters as tappable rows */
    const openAllClusters = (title: string, clusters: ClusterItem[], color: string) => {
        // Flatten all contacts from all clusters for the ClusterView
        const all = clusters.flatMap((cl) => cl.contacts);
        // Deduplicate by id
        const seen = new Set<string>();
        const unique = all.filter((c) => {
            if (seen.has(c.id)) return false;
            seen.add(c.id);
            return true;
        });
        setActiveCluster({
            title,
            contacts: unique,
            color,
            subtitle: `${clusters.length} groups`,
            clusters,
        });
    };

    return (
        <>
            {activeCluster &&
                (activeCluster.clusters ? (
                    <ClusterListView
                        title={activeCluster.title}
                        clusters={activeCluster.clusters}
                        accentColor={activeCluster.color}
                        onSelectCluster={(cl) =>
                            setActiveCluster({
                                title: cl.label,
                                contacts: cl.contacts,
                                color: activeCluster.color,
                            })
                        }
                        onBack={() => setActiveCluster(null)}
                    />
                ) : (
                    <ClusterView
                        title={activeCluster.title}
                        subtitle={activeCluster.subtitle}
                        contacts={activeCluster.contacts}
                        accentColor={activeCluster.color}
                        onSelect={onSelectContact}
                        onBack={() => setActiveCluster(null)}
                    />
                ))}

            <div className="flex flex-col gap-10 pb-28">
                {/* ═══ 1. HERO CTA ═══ */}
                <HeroCTA />

                {/* ═══ 2. FAVORITES — Horizontal scroll album cards ═══ */}
                {feed.favorites.length > 0 && (
                    <Sec title="Favorites" icon={<Star size={16} />} accent={C.gold}>
                        <div className="no-scrollbar flex gap-4 overflow-x-auto px-5">
                            {feed.favorites.map((c) => {
                                const [bg1, bg2] = getAvatarColors(c);
                                const role = getRoleDisplay(c);
                                return (
                                    <button
                                        key={c.id}
                                        onClick={() => onSelectContact(c)}
                                        className="relative w-[160px] shrink-0 overflow-hidden rounded-2xl transition-all active:scale-[0.97]"
                                        style={{
                                            background: c.photo
                                                ? undefined
                                                : `linear-gradient(145deg, ${bg1}, ${bg2})`,
                                            minHeight: 200,
                                        }}
                                    >
                                        {c.photo && (
                                            <>
                                                <img
                                                    src={c.photo}
                                                    alt=""
                                                    className="absolute inset-0 h-full w-full object-cover"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                            </>
                                        )}
                                        {!c.photo && (
                                            <div
                                                className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-25 blur-xl"
                                                style={{
                                                    background: "rgba(255,255,255,0.5)",
                                                }}
                                            />
                                        )}
                                        {/* Heart */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onToggleFavorite(c.id);
                                            }}
                                            className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/30 backdrop-blur-sm active:scale-90"
                                        >
                                            <Heart
                                                size={12}
                                                className="fill-amber-400 text-amber-400"
                                            />
                                        </button>
                                        <div className="relative flex h-full flex-col justify-end p-3.5">
                                            <p className="text-[15px] font-bold text-white drop-shadow-lg">
                                                {getFullName(c)}
                                            </p>
                                            {role && (
                                                <p className="mt-0.5 truncate text-[11px] text-white/70 drop-shadow">
                                                    {role}
                                                </p>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </Sec>
                )}

                {/* ═══ 3. RECENTLY VIEWED — Horizontal scroll ═══ */}
                {feed.recentlyViewed.length > 0 && (
                    <Sec
                        title="Recently Viewed"
                        icon={<Clock size={16} />}
                        accent={C.violet}
                    >
                        <div className="no-scrollbar flex gap-4 overflow-x-auto px-5">
                            {feed.recentlyViewed.slice(0, 8).map((c) => {
                                const [bg1, bg2] = getAvatarColors(c);
                                const role = getRoleDisplay(c);
                                const ago = c.lastViewedAt ? timeAgo(c.lastViewedAt) : "";
                                return (
                                    <button
                                        key={c.id}
                                        onClick={() => onSelectContact(c)}
                                        className="relative w-[170px] shrink-0 overflow-hidden rounded-2xl transition-all active:scale-[0.97]"
                                        style={{
                                            background: `linear-gradient(160deg, ${bg1}, ${bg2})`,
                                            minHeight: 210,
                                        }}
                                    >
                                        <div
                                            className="absolute -right-8 -top-8 h-28 w-28 rounded-full opacity-30 blur-2xl"
                                            style={{
                                                background: "rgba(255,255,255,0.4)",
                                            }}
                                        />
                                        <div className="relative flex h-full flex-col items-center justify-center px-4 pb-4 pt-6">
                                            <ContactAvatar
                                                contact={c}
                                                size={68}
                                                className="shadow-xl ring-2 ring-white/20"
                                            />
                                            <p className="mt-3 w-full truncate text-center text-[15px] font-bold text-white">
                                                {getFullName(c)}
                                            </p>
                                            {role && (
                                                <p className="mt-1 w-full truncate text-center text-[11px] text-white/60">
                                                    {role}
                                                </p>
                                            )}
                                            {ago && (
                                                <span className="mt-2 rounded-full bg-black/20 px-2.5 py-0.5 text-[10px] font-medium text-white/70">
                                                    {ago}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </Sec>
                )}

                {/* ═══ 4. BIRTHDAYS THIS WEEK — Gradient celebration cards ═══ */}
                {feed.birthdaysSoon.length > 0 && (
                    <Sec
                        title={
                            feed.birthdaysSoon.some((b) => b.daysAway === 0)
                                ? "Happy Birthday!"
                                : "Birthdays This Week"
                        }
                        icon={<Cake size={16} />}
                        accent={C.pink}
                    >
                        <div className="no-scrollbar flex gap-4 overflow-x-auto px-5">
                            {feed.birthdaysSoon.map((b) => {
                                const [bg1, bg2] = getAvatarColors(b.contact);
                                const isToday = b.daysAway === 0;
                                return (
                                    <button
                                        key={b.contact.id}
                                        onClick={() => onSelectContact(b.contact)}
                                        className="relative w-[150px] shrink-0 overflow-hidden rounded-2xl transition-all active:scale-[0.97]"
                                        style={{
                                            background: isToday
                                                ? "linear-gradient(160deg, #EC4899, #F97316)"
                                                : `linear-gradient(160deg, ${bg1}, ${bg2})`,
                                            minHeight: 190,
                                        }}
                                    >
                                        {/* Confetti for today */}
                                        {isToday && (
                                            <>
                                                <div className="absolute left-3 top-4 h-2 w-2 animate-pulse rounded-full bg-yellow-300/60" />
                                                <div
                                                    className="absolute right-6 top-6 h-1.5 w-1.5 animate-pulse rounded-full bg-white/40"
                                                    style={{
                                                        animationDelay: "200ms",
                                                    }}
                                                />
                                                <div
                                                    className="absolute left-8 top-3 h-1 w-1 animate-pulse rounded-full bg-cyan-300/50"
                                                    style={{
                                                        animationDelay: "400ms",
                                                    }}
                                                />
                                            </>
                                        )}
                                        {/* Decorative circles */}
                                        <div className="absolute -right-4 -top-4 h-16 w-16 rounded-full border-[2px] border-white/10" />
                                        <div className="absolute -bottom-3 -left-3 h-12 w-12 rounded-full border-[2px] border-white/[0.06]" />
                                        <div className="relative flex h-full flex-col items-center justify-center px-3 pb-4 pt-5">
                                            <ContactAvatar
                                                contact={b.contact}
                                                size={64}
                                                className="ring-2 ring-white/25"
                                            />
                                            <p className="mt-3 w-full truncate text-center text-[15px] font-bold text-white">
                                                {b.contact.firstName}
                                            </p>
                                            <p className="mt-1 text-[13px] font-semibold text-white/80">
                                                {isToday
                                                    ? "Today!"
                                                    : b.daysAway <= 3
                                                      ? `In ${b.daysAway} days`
                                                      : b.label}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </Sec>
                )}

                {/* ═══ 5. WHERE THEY ARE — Grid with abstract geometry ═══ */}
                {feed.locationClusters.length > 0 && (
                    <Sec
                        title="Where They Are"
                        icon={<MapPin size={16} />}
                        accent={C.ocean}
                    >
                        <div className="grid grid-cols-2 gap-3 px-5">
                            {feed.locationClusters.slice(0, 6).map((cluster, i) => {
                                const [g1, g2] = G.location[i % G.location.length];
                                return (
                                    <button
                                        key={cluster.label}
                                        onClick={() =>
                                            open(cluster.label, cluster.contacts, g1)
                                        }
                                        className="relative overflow-hidden rounded-2xl transition-all active:scale-[0.97]"
                                        style={{
                                            background: `linear-gradient(135deg, ${g1}, ${g2})`,
                                            minHeight: 130,
                                        }}
                                    >
                                        {/* Soft glowing orbs */}
                                        <div
                                            className="absolute rounded-full bg-white/[0.09] blur-sm"
                                            style={{
                                                width: 50 + (i % 3) * 15,
                                                height: 50 + (i % 3) * 15,
                                                right: i % 2 === 0 ? -12 : "auto",
                                                left: i % 2 === 1 ? -12 : "auto",
                                                top: -12,
                                            }}
                                        />
                                        <div
                                            className="absolute rounded-full bg-white/[0.05]"
                                            style={{
                                                width: 35,
                                                height: 35,
                                                right: i % 2 === 0 ? 15 : -8,
                                                bottom: i % 2 === 0 ? -8 : 15,
                                            }}
                                        />
                                        <div className="relative flex h-full flex-col justify-end p-4">
                                            <p className="text-[22px] font-extrabold tracking-tight text-white drop-shadow">
                                                {cluster.label}
                                            </p>
                                            <p className="mt-0.5 text-[13px] font-medium text-white/70">
                                                {cluster.count}{" "}
                                                {cluster.count === 1
                                                    ? "person"
                                                    : "people"}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        {feed.locationClusters.length > 6 && (
                            <button
                                onClick={() =>
                                    openAllClusters(
                                        "All Locations",
                                        feed.locationClusters,
                                        G.location[0][0]
                                    )
                                }
                                className="mx-5 mt-3 flex items-center gap-1 text-[13px] font-medium text-white/40"
                            >
                                See all {feed.locationClusters.length}{" "}
                                <ChevronRight size={13} />
                            </button>
                        )}
                    </Sec>
                )}

                {/* ═══ 6. INDUSTRIES — Ranked list (different layout!) ═══ */}
                {feed.industryClusters.length > 0 && (
                    <Sec
                        title="Industries"
                        icon={<TrendingUp size={16} />}
                        accent={C.teal}
                    >
                        <div className="px-5">
                            {feed.industryClusters.slice(0, 6).map((cluster, i) => {
                                const [g1] = G.industry[i % G.industry.length];
                                const preview = cluster.contacts.slice(0, 3);
                                return (
                                    <button
                                        key={cluster.label}
                                        onClick={() =>
                                            open(cluster.label, cluster.contacts, g1)
                                        }
                                        className={`flex w-full items-center gap-4 py-3.5 text-left transition-all active:opacity-70 ${i < Math.min(5, feed.industryClusters.length - 1) ? "border-b border-white/[0.05]" : ""}`}
                                    >
                                        {/* Color accent bar */}
                                        <div
                                            className="h-10 w-1 shrink-0 rounded-full"
                                            style={{
                                                background: g1,
                                            }}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[15px] font-semibold text-white">
                                                {cluster.label}
                                            </p>
                                            <p className="text-[12px] text-white/40">
                                                {cluster.count} people
                                            </p>
                                        </div>
                                        <div className="flex -space-x-2">
                                            {preview.map((c) => (
                                                <div
                                                    key={c.id}
                                                    className="rounded-full border-2 border-[hsl(0,0%,7%)]"
                                                >
                                                    <ContactAvatar
                                                        contact={c}
                                                        size={30}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <ChevronRight
                                            size={14}
                                            className="shrink-0 text-white/15"
                                        />
                                    </button>
                                );
                            })}
                            {feed.industryClusters.length > 6 && (
                                <button
                                    onClick={() =>
                                        openAllClusters(
                                            "All Industries",
                                            feed.industryClusters,
                                            G.industry[0][0]
                                        )
                                    }
                                    className="mt-2 flex items-center gap-1 text-[13px] font-medium text-white/40"
                                >
                                    See all {feed.industryClusters.length}{" "}
                                    <ChevronRight size={13} />
                                </button>
                            )}
                        </div>
                    </Sec>
                )}

                {/* ═══ 7. WHAT THEY DO — Horizontal scroll cards ═══ */}
                {feed.titleClusters.length > 0 && (
                    <Sec
                        title="What They Do"
                        icon={<Users size={16} />}
                        accent={C.emerald}
                    >
                        <div className="no-scrollbar flex gap-3 overflow-x-auto px-5">
                            {feed.titleClusters.slice(0, 12).map((cluster, i) => {
                                const [g1, g2] = G.role[i % G.role.length];
                                return (
                                    <button
                                        key={cluster.label}
                                        onClick={() =>
                                            open(cluster.label, cluster.contacts, g1)
                                        }
                                        className="relative h-[100px] w-[140px] shrink-0 overflow-hidden rounded-2xl transition-all active:scale-[0.97]"
                                        style={{
                                            background: `linear-gradient(145deg, ${g1}, ${g2})`,
                                        }}
                                    >
                                        <div className="absolute -right-6 bottom-0 top-0 w-12 -skew-x-12 bg-white/[0.07]" />
                                        <div className="relative flex h-full flex-col justify-between p-3.5">
                                            <span className="text-[32px] font-black leading-none text-white/25">
                                                {cluster.count}
                                            </span>
                                            <p className="text-[14px] font-bold text-white">
                                                {cluster.label}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        {feed.titleClusters.length > 12 && (
                            <button
                                onClick={() =>
                                    openAllClusters(
                                        "All Roles",
                                        feed.titleClusters,
                                        G.role[0][0]
                                    )
                                }
                                className="mx-5 mt-3 flex items-center gap-1 text-[13px] font-medium text-white/40"
                            >
                                See all {feed.titleClusters.length}{" "}
                                <ChevronRight size={13} />
                            </button>
                        )}
                    </Sec>
                )}

                {/* ═══ 8. WHERE THEY WORK — Horizontal large cards ═══ */}
                {feed.companyClusters.length > 0 && (
                    <Sec
                        title="Where They Work"
                        icon={<Briefcase size={16} />}
                        accent={C.violet}
                    >
                        <div className="no-scrollbar flex gap-4 overflow-x-auto px-5">
                            {feed.companyClusters.slice(0, 10).map((cluster, i) => {
                                const [g1, g2] = G.company[i % G.company.length];
                                const preview = cluster.contacts.slice(0, 4);
                                return (
                                    <button
                                        key={cluster.label}
                                        onClick={() =>
                                            open(cluster.label, cluster.contacts, g1)
                                        }
                                        className="relative w-[190px] shrink-0 overflow-hidden rounded-2xl transition-all active:scale-[0.97]"
                                        style={{
                                            background: `linear-gradient(160deg, ${g1}, ${g2})`,
                                            minHeight: 150,
                                        }}
                                    >
                                        <div
                                            className="absolute -right-10 -top-10 h-24 w-24 rounded-full opacity-20 blur-2xl"
                                            style={{ background: "white" }}
                                        />
                                        <div className="relative flex h-full flex-col items-center justify-center p-4 text-center">
                                            <p className="w-full text-[17px] font-extrabold leading-tight text-white">
                                                {cluster.label}
                                            </p>
                                            <div className="mt-3 flex items-center -space-x-2">
                                                {preview.map((c) => (
                                                    <div
                                                        key={c.id}
                                                        className="rounded-full border-2 border-black/20"
                                                    >
                                                        <ContactAvatar
                                                            contact={c}
                                                            size={30}
                                                        />
                                                    </div>
                                                ))}
                                                {cluster.count > 4 && (
                                                    <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-black/20 bg-white/15 backdrop-blur-sm">
                                                        <span className="text-[9px] font-bold text-white/80">
                                                            +{cluster.count - 4}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <p className="mt-2 text-[12px] font-medium text-white/60">
                                                {cluster.count} people
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        {feed.companyClusters.length > 10 && (
                            <button
                                onClick={() =>
                                    openAllClusters(
                                        "All Companies",
                                        feed.companyClusters,
                                        G.company[0][0]
                                    )
                                }
                                className="mx-5 mt-3 flex items-center gap-1 text-[13px] font-medium text-white/40"
                            >
                                See all {feed.companyClusters.length}{" "}
                                <ChevronRight size={13} />
                            </button>
                        )}
                    </Sec>
                )}

                {/* ═══ 9. REDISCOVER — Horizontal person cards ═══ */}
                {feed.rediscover.length > 0 && (
                    <Sec
                        title="Rediscover"
                        icon={<Shuffle size={16} />}
                        accent={C.warmOrange}
                    >
                        <div className="no-scrollbar flex gap-4 overflow-x-auto px-5">
                            {feed.rediscover.map((c) => {
                                const [bg1, bg2] = getAvatarColors(c);
                                const role = getRoleDisplay(c);
                                return (
                                    <button
                                        key={c.id}
                                        onClick={() => onSelectContact(c)}
                                        className="relative w-[150px] shrink-0 overflow-hidden rounded-2xl transition-all active:scale-[0.97]"
                                        style={{
                                            background: `linear-gradient(170deg, ${bg1}, ${bg2})`,
                                            minHeight: 200,
                                        }}
                                    >
                                        {/* Decorative orbs */}
                                        <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-white/[0.08] blur-sm" />
                                        <div className="absolute -bottom-3 -left-3 h-14 w-14 rounded-full bg-white/[0.05]" />
                                        <div className="absolute bottom-16 right-3 h-6 w-6 rounded-full bg-white/[0.06]" />
                                        <div className="relative flex h-full flex-col items-center justify-center px-3 pb-4 pt-5">
                                            {c.photo ? (
                                                <img
                                                    src={c.photo}
                                                    alt=""
                                                    className="h-[80px] w-[80px] shrink-0 rounded-2xl object-cover shadow-lg ring-2 ring-white/20"
                                                />
                                            ) : (
                                                <ContactAvatar
                                                    contact={c}
                                                    size={80}
                                                    className="shadow-lg ring-2 ring-white/20"
                                                />
                                            )}
                                            <p className="mt-3 w-full truncate text-center text-[15px] font-bold text-white">
                                                {c.firstName} {c.lastName}
                                            </p>
                                            {role && (
                                                <p className="mt-1 w-full truncate text-center text-[11px] text-white/50">
                                                    {role}
                                                </p>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </Sec>
                )}

                {/* ═══ 10. MOST VISITED — Ranked list ═══ */}
                {feed.mostVisited.length > 0 && (
                    <Sec title="Most Visited" icon={<Eye size={16} />} accent={C.violet}>
                        <div className="px-5">
                            {feed.mostVisited.slice(0, 5).map((c, i) => {
                                const role = getRoleDisplay(c);
                                return (
                                    <button
                                        key={c.id}
                                        onClick={() => onSelectContact(c)}
                                        className={`flex w-full items-center gap-4 py-3.5 text-left transition-all active:opacity-70 ${i < Math.min(4, feed.mostVisited.length - 1) ? "border-b border-white/[0.05]" : ""}`}
                                    >
                                        <span className="w-5 text-right text-[16px] font-bold tabular-nums text-white/20">
                                            {i + 1}
                                        </span>
                                        <ContactAvatar contact={c} size={48} />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-[15px] font-semibold text-white">
                                                {getFullName(c)}
                                            </p>
                                            {role && (
                                                <p className="mt-0.5 truncate text-[12px] text-white/40">
                                                    {role}
                                                </p>
                                            )}
                                        </div>
                                        <span
                                            className="rounded-full px-2.5 py-1 text-[12px] font-bold"
                                            style={{
                                                background: `${C.violet}20`,
                                                color: C.violet,
                                            }}
                                        >
                                            {c.viewCount}x
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </Sec>
                )}

                {/* ═══ 11. SKILLS — Vibrant horizontal scroll cards ═══ */}
                {feed.skillClusters.length > 0 && (
                    <Sec
                        title="Skills in Your Network"
                        icon={<Zap size={16} />}
                        accent={C.coral}
                    >
                        <div className="no-scrollbar flex gap-3 overflow-x-auto px-5">
                            {feed.skillClusters.slice(0, 12).map((cluster, i) => {
                                const [g1, g2] = G.skill[i % G.skill.length];
                                return (
                                    <button
                                        key={cluster.label}
                                        onClick={() =>
                                            open(cluster.label, cluster.contacts, g1)
                                        }
                                        className="relative shrink-0 overflow-hidden rounded-2xl transition-all active:scale-[0.97]"
                                        style={{
                                            background: `linear-gradient(135deg, ${g1}, ${g2})`,
                                            minWidth: 140,
                                            minHeight: 75,
                                        }}
                                    >
                                        <div className="absolute -right-3 -top-3 h-10 w-10 rounded-full bg-white/10" />
                                        <div className="relative flex h-full flex-col justify-center p-4">
                                            <p className="text-[14px] font-bold text-white">
                                                {cluster.label}
                                            </p>
                                            <p className="mt-0.5 text-[11px] text-white/60">
                                                {cluster.count} people
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        {feed.skillClusters.length > 12 && (
                            <button
                                onClick={() =>
                                    openAllClusters(
                                        "All Skills",
                                        feed.skillClusters,
                                        G.skill[0][0]
                                    )
                                }
                                className="mx-5 mt-3 flex items-center gap-1 text-[13px] font-medium text-white/40"
                            >
                                See all {feed.skillClusters.length}{" "}
                                <ChevronRight size={13} />
                            </button>
                        )}
                    </Sec>
                )}

                {/* ═══ 12. WHERE THEY STUDIED — Horizontal scroll ═══ */}
                {feed.schoolClusters.length > 0 && (
                    <Sec
                        title="Where They Studied"
                        icon={<GraduationCap size={16} />}
                        accent={C.ocean}
                    >
                        <div className="no-scrollbar flex gap-4 overflow-x-auto px-5">
                            {feed.schoolClusters.map((cluster, i) => {
                                const [g1, g2] = G.school[i % G.school.length];
                                return (
                                    <button
                                        key={cluster.label}
                                        onClick={() =>
                                            open(cluster.label, cluster.contacts, g1)
                                        }
                                        className="relative w-[190px] shrink-0 overflow-hidden rounded-2xl transition-all active:scale-[0.97]"
                                        style={{
                                            background: `linear-gradient(160deg, ${g1}, ${g2})`,
                                            minHeight: 110,
                                        }}
                                    >
                                        <div className="absolute -right-3 top-3 h-16 w-8 rotate-12 rounded-full bg-white/[0.08]" />
                                        <div className="relative flex h-full flex-col justify-end p-4">
                                            <p className="text-[15px] font-bold leading-tight text-white">
                                                {cluster.label}
                                            </p>
                                            <p className="mt-1 text-[12px] text-white/50">
                                                {cluster.count} alumni
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </Sec>
                )}

                {/* ═══ 13. YOUR TAGS — Wrapped pills with color ═══ */}
                {feed.tagClusters.length > 0 && (
                    <Sec title="Your Tags" icon={<Tag size={16} />} accent={C.gold}>
                        <div className="flex flex-wrap gap-2.5 px-5">
                            {feed.tagClusters.slice(0, 15).map((cluster, i) => {
                                const [g1, g2] = G.skill[i % G.skill.length];
                                return (
                                    <button
                                        key={cluster.label}
                                        onClick={() =>
                                            open(
                                                `#${cluster.label}`,
                                                cluster.contacts,
                                                g1
                                            )
                                        }
                                        className="flex items-center gap-2 rounded-full px-4 py-2.5 transition-all active:scale-95"
                                        style={{
                                            background: `linear-gradient(135deg, ${g1}30, ${g2}15)`,
                                            border: `1px solid ${g1}40`,
                                        }}
                                    >
                                        <span
                                            className="text-[13px] font-semibold"
                                            style={{ color: g1 }}
                                        >
                                            #{cluster.label}
                                        </span>
                                        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white/10 px-1.5 text-[10px] font-medium text-white/50">
                                            {cluster.count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </Sec>
                )}

                {/* ═══ 14. UPCOMING BIRTHDAYS — Gradient cards ═══ */}
                {feed.birthdaysLater.length > 0 && (
                    <Sec
                        title="Upcoming Birthdays"
                        icon={<Cake size={16} />}
                        accent={C.rose}
                    >
                        <div className="no-scrollbar flex gap-3 overflow-x-auto px-5">
                            {feed.birthdaysLater.slice(0, 10).map((b) => {
                                const [bg1, bg2] = getAvatarColors(b.contact);
                                return (
                                    <button
                                        key={b.contact.id}
                                        onClick={() => onSelectContact(b.contact)}
                                        className="relative w-[130px] shrink-0 overflow-hidden rounded-2xl transition-all active:scale-[0.97]"
                                        style={{
                                            background: `linear-gradient(160deg, ${bg1}, ${bg2})`,
                                            minHeight: 160,
                                        }}
                                    >
                                        <div className="absolute -right-4 -top-4 h-14 w-14 rounded-full bg-white/[0.08]" />
                                        <div className="absolute -bottom-2 -left-2 h-10 w-10 rounded-full bg-white/[0.05]" />
                                        <div className="relative flex h-full flex-col items-center justify-center px-3 pb-3 pt-4">
                                            <ContactAvatar
                                                contact={b.contact}
                                                size={52}
                                            />
                                            <p className="mt-2 w-full truncate text-center text-[13px] font-semibold text-white">
                                                {b.contact.firstName}
                                            </p>
                                            <p className="mt-1 text-[12px] font-medium text-white/70">
                                                {b.label}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </Sec>
                )}

                {/* ═══ 15. RECENTLY UPDATED — List ═══ */}
                {feed.recentlyUpdated.length > 0 && (
                    <Sec
                        title="Recently Updated"
                        icon={<Clock size={16} />}
                        accent={C.emerald}
                    >
                        <div className="px-5">
                            {feed.recentlyUpdated.slice(0, 5).map((c, i) => {
                                const role = getRoleDisplay(c);
                                const ago = timeAgo(c.updatedAt);
                                return (
                                    <button
                                        key={c.id}
                                        onClick={() => onSelectContact(c)}
                                        className={`flex w-full items-center gap-4 py-3.5 text-left transition-all active:opacity-70 ${i < Math.min(4, feed.recentlyUpdated.length - 1) ? "border-b border-white/[0.05]" : ""}`}
                                    >
                                        <ContactAvatar contact={c} size={44} />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-[15px] font-semibold text-white">
                                                {getFullName(c)}
                                            </p>
                                            {role && (
                                                <p className="mt-0.5 truncate text-[12px] text-white/40">
                                                    {role}
                                                </p>
                                            )}
                                        </div>
                                        <span className="text-[11px] text-white/30">
                                            {ago}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </Sec>
                )}

                {/* ═══ 16. NETWORK STATS ═══ */}
                <div className="mx-5">
                    <div className="mb-4 flex items-center gap-2.5">
                        <BarChart3 size={16} className="text-white/50" />
                        <h2 className="text-[17px] font-bold text-white">Your Network</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <StatCard
                            value={contacts.length}
                            label="People"
                            gradient={["#3B82F6", "#6366F1"]}
                        />
                        <StatCard
                            value={contacts.filter((c) => c.claimed).length}
                            label="On Meridian"
                            gradient={["#10B981", "#059669"]}
                        />
                        <StatCard
                            value={feed.locationClusters.length}
                            label="Cities"
                            gradient={["#F59E0B", "#D97706"]}
                        />
                        <StatCard
                            value={feed.companyClusters.length}
                            label="Companies"
                            gradient={["#8B5CF6", "#7C3AED"]}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

// ─── Section Components ─────────────────────────────────────────────────────

function Sec({
    title,
    icon,
    accent,
    children,
}: {
    title: string;
    icon: React.ReactNode;
    accent: string;
    children: React.ReactNode;
}) {
    return (
        <div>
            <div className="mb-4 flex items-center gap-2.5 px-5">
                <span style={{ color: accent }}>{icon}</span>
                <h2 className="text-[17px] font-bold text-white">{title}</h2>
            </div>
            {children}
        </div>
    );
}

function StatCard({
    value,
    label,
    gradient,
}: {
    value: number;
    label: string;
    gradient: [string, string];
}) {
    return (
        <div
            className="rounded-2xl p-4"
            style={{
                background: `linear-gradient(135deg, ${gradient[0]}15, ${gradient[1]}08)`,
                border: `1px solid ${gradient[0]}20`,
            }}
        >
            <p className="text-[28px] font-black" style={{ color: gradient[0] }}>
                {value.toLocaleString()}
            </p>
            <p className="mt-0.5 text-[12px] font-medium text-white/40">{label}</p>
        </div>
    );
}

// ─── Hero CTA ───────────────────────────────────────────────────────────────

function HeroCTA() {
    return (
        <button
            className="relative mx-5 overflow-hidden rounded-3xl transition-all active:scale-[0.98]"
            style={{
                background: "linear-gradient(145deg, #F59E0B, #F97316, #EF4444)",
                minHeight: 180,
            }}
        >
            <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-8 -left-8 h-28 w-28 rounded-full bg-yellow-200/15 blur-xl" />
            <div className="absolute bottom-6 right-8 h-16 w-16 rounded-full bg-white/[0.07] blur-lg" />
            <div className="relative flex h-full flex-col justify-end p-6">
                <div className="mb-3 flex items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                        <Mic size={18} className="text-white" />
                    </div>
                    <div className="h-3 w-3 animate-pulse rounded-full bg-white/60" />
                </div>
                <p className="text-[26px] font-extrabold leading-tight tracking-tight text-white">
                    Know Your People Better
                </p>
                <p className="mt-2 text-[14px] font-medium text-white/70">
                    Start a session to enrich your contacts
                </p>
            </div>
        </button>
    );
}

// ─── Cluster List View (for "See All" of grouped data) ──────────────────────

function ClusterListView({
    title,
    clusters,
    accentColor,
    onSelectCluster,
    onBack,
}: {
    title: string;
    clusters: ClusterItem[];
    accentColor: string;
    onSelectCluster: (cluster: ClusterItem) => void;
    onBack: () => void;
}) {
    const [mounted, setMounted] = useState(false);

    useState(() => {
        requestAnimationFrame(() => setMounted(true));
        document.body.style.overflow = "hidden";
    });

    // Clean up on unmount
    useMemo(() => {
        return () => {
            document.body.style.overflow = "";
        };
    }, []);

    const handleBack = () => {
        setMounted(false);
        document.body.style.overflow = "";
        setTimeout(onBack, 280);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
            style={{
                backgroundColor: "hsl(0 0% 7%)",
                transform: mounted ? "translateY(0)" : "translateY(100%)",
            }}
        >
            <div
                className="relative shrink-0 overflow-hidden"
                style={{
                    background: `linear-gradient(180deg, ${accentColor}35 0%, ${accentColor}15 50%, transparent 100%)`,
                }}
            >
                <div className="h-[env(safe-area-inset-top,0px)]" />
                <div
                    className="absolute -right-12 -top-12 h-56 w-56 rounded-full opacity-30 blur-3xl"
                    style={{ backgroundColor: accentColor }}
                />
                <div className="relative px-5 pb-6 pt-3">
                    <button
                        onClick={handleBack}
                        className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.08] backdrop-blur-sm active:scale-95"
                    >
                        <span className="text-[18px] text-white">←</span>
                    </button>
                    <h1
                        className="text-[34px] font-extrabold tracking-tight"
                        style={{ color: accentColor }}
                    >
                        {title}
                    </h1>
                    <p className="mt-1 text-[15px] text-white/40">
                        {clusters.length} groups
                    </p>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-32">
                {clusters.map((cluster, i) => {
                    const preview = cluster.contacts.slice(0, 3);
                    return (
                        <button
                            key={cluster.label}
                            onClick={() => onSelectCluster(cluster)}
                            className={`flex w-full items-center gap-4 py-3.5 text-left transition-all active:opacity-70 ${i < clusters.length - 1 ? "border-b border-white/[0.05]" : ""}`}
                        >
                            <div
                                className="h-10 w-1 shrink-0 rounded-full"
                                style={{ background: accentColor }}
                            />
                            <div className="min-w-0 flex-1">
                                <p className="text-[16px] font-semibold text-white">
                                    {cluster.label}
                                </p>
                                <p className="text-[13px] text-white/40">
                                    {cluster.count}{" "}
                                    {cluster.count === 1 ? "person" : "people"}
                                </p>
                            </div>
                            <div className="flex -space-x-2">
                                {preview.map((c) => (
                                    <div
                                        key={c.id}
                                        className="rounded-full border-2 border-[hsl(0,0%,7%)]"
                                    >
                                        <ContactAvatar contact={c} size={28} />
                                    </div>
                                ))}
                            </div>
                            <ChevronRight size={14} className="shrink-0 text-white/15" />
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days}d ago`;
    return `${Math.floor(days / 7)}w ago`;
}

// ─── Data Builder ───────────────────────────────────────────────────────────

function buildFeedData(contacts: Contact[]) {
    const daySeed = Math.floor(Date.now() / 86400000);
    const favorites = contacts.filter((c) => c.favorite);

    // Birthdays
    const birthdays: BirthdayEntry[] = [];
    const now = new Date();
    for (const c of contacts) {
        if (!c.birthday) continue;
        const bd = parseBday(c.birthday);
        if (!bd) continue;
        const thisYear = new Date(now.getFullYear(), bd.m - 1, bd.d);
        if (thisYear < now) thisYear.setFullYear(thisYear.getFullYear() + 1);
        const days = Math.floor((thisYear.getTime() - now.getTime()) / 86400000);
        if (days <= 90)
            birthdays.push({
                contact: c,
                label: thisYear.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                }),
                daysAway: days,
                month: thisYear.toLocaleDateString("en-US", { month: "long" }),
            });
    }
    birthdays.sort((a, b) => a.daysAway - b.daysAway);
    const birthdaysSoon = birthdays.filter((b) => b.daysAway <= 7);
    const birthdaysLater = birthdays.filter((b) => b.daysAway > 7);

    const recentlyViewed = [...contacts]
        .filter((c) => c.lastViewedAt)
        .sort((a, b) => (b.lastViewedAt ?? "").localeCompare(a.lastViewedAt ?? ""))
        .slice(0, 10);
    const mostVisited = [...contacts]
        .filter((c) => (c.viewCount ?? 0) > 0)
        .sort((a, b) => (b.viewCount ?? 0) - (a.viewCount ?? 0))
        .slice(0, 10);
    const recentlyUpdated = [...contacts]
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        .slice(0, 10);
    const shuffled = [...contacts]
        .filter((c) => !c.favorite)
        .sort((a, b) => (hash(a.id + daySeed) % 1000) - (hash(b.id + daySeed) % 1000));
    const rediscover = shuffled.slice(0, 10);

    const locationClusters = sorted(
        group(contacts, (c) => (c.addresses[0]?.city ?? c.currentCity)?.trim() || null),
        1,
        30
    );
    const companyClusters = sorted(
        group(contacts, (c) => c.jobs.find((j) => j.current)?.company?.trim() || null),
        2,
        50
    );
    const industryClusters = sorted(
        group(
            contacts,
            (c) => (c.jobs.find((j) => j.current) ?? c.jobs[0])?.industry?.trim() || null
        ),
        1,
        30
    );
    const titleClusters = sorted(
        group(contacts, (c) => {
            const j = c.jobs.find((j) => j.current);
            return j?.title ? normTitle(j.title) || null : null;
        }),
        2,
        30
    );

    const sm = new Map<string, Contact[]>();
    for (const c of contacts)
        for (const s of c.skills) {
            const k = s.trim().toLowerCase();
            if (!sm.has(k)) sm.set(k, []);
            sm.get(k)!.push(c);
        }
    const skillClusters = sorted(sm, 1, 30);

    const scm = new Map<string, Contact[]>();
    for (const c of contacts)
        for (const e of c.education) {
            if (e.school) {
                const k = e.school.trim();
                if (!scm.has(k)) scm.set(k, []);
                scm.get(k)!.push(c);
            }
        }
    const schoolClusters = sorted(scm, 1, 20);

    const tm = new Map<string, Contact[]>();
    for (const c of contacts)
        for (const t of c.tags) {
            const k = t.trim().toLowerCase();
            if (!tm.has(k)) tm.set(k, []);
            tm.get(k)!.push(c);
        }
    const tagClusters = sorted(tm, 1, 30);

    return {
        favorites,
        birthdaysSoon,
        birthdaysLater,
        recentlyViewed,
        mostVisited,
        recentlyUpdated,
        rediscover,
        locationClusters,
        companyClusters,
        industryClusters,
        titleClusters,
        skillClusters,
        schoolClusters,
        tagClusters,
    };
}

function hash(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
    return Math.abs(h);
}
function group(cs: Contact[], fn: (c: Contact) => string | null): Map<string, Contact[]> {
    const m = new Map<string, Contact[]>();
    for (const c of cs) {
        const k = fn(c);
        if (k) {
            if (!m.has(k)) m.set(k, []);
            m.get(k)!.push(c);
        }
    }
    return m;
}
function sorted(m: Map<string, Contact[]>, min: number, max: number): ClusterItem[] {
    return Array.from(m.entries())
        .filter(([, c]) => c.length >= min)
        .sort((a, b) => b[1].length - a[1].length)
        .slice(0, max)
        .map(([l, c]) => ({ label: l, count: c.length, contacts: c }));
}
function parseBday(bd: string): { m: number; d: number } | null {
    const r = bd.match(/(\d{1,2})-(\d{1,2})$/);
    return r ? { m: parseInt(r[1], 10), d: parseInt(r[2], 10) } : null;
}

function normTitle(t: string): string {
    const l = t.toLowerCase();
    if (l.includes("ceo") || l.includes("chief executive")) return "CEOs";
    if (l.includes("cto") || l.includes("chief tech")) return "CTOs";
    if (l.includes("cfo") || l.includes("chief financial")) return "CFOs";
    if (l.includes("coo") || l.includes("chief operating")) return "COOs";
    if (l.includes("ciso")) return "CISOs";
    if (l.includes("vp") || l.includes("vice president")) return "VPs";
    if (l.includes("director")) return "Directors";
    if (l.includes("manager")) return "Managers";
    if (l.includes("engineer") || l.includes("developer")) return "Engineers";
    if (l.includes("designer")) return "Designers";
    if (l.includes("sales") || l.includes("account exec")) return "Sales";
    if (l.includes("marketing")) return "Marketing";
    if (l.includes("recruit")) return "Recruiters";
    if (l.includes("consult")) return "Consultants";
    if (l.includes("attorney") || l.includes("lawyer")) return "Legal";
    if (l.includes("nurse") || l.includes("doctor") || l.includes("physician"))
        return "Healthcare";
    if (l.includes("teacher") || l.includes("professor")) return "Education";
    if (l.includes("owner") || l.includes("founder")) return "Founders";
    if (l.includes("analyst")) return "Analysts";
    if (l.includes("partner")) return "Partners";
    if (l.includes("real estate") || l.includes("realtor") || l.includes("broker"))
        return "Real Estate";
    if (l.includes("therapist") || l.includes("psychol")) return "Therapists";
    if (l.includes("coach")) return "Coaches";
    if (l.includes("president")) return "Presidents";
    if (l.includes("admin") || l.includes("assistant")) return "Admin";
    if (l.includes("intern")) return "Interns";
    if (l.includes("freelanc")) return "Freelancers";
    if (l.includes("scientist") || l.includes("research")) return "Researchers";
    if (l.includes("product")) return "Product";
    if (l.includes("writer") || l.includes("editor") || l.includes("content"))
        return "Content";
    if (l.includes("operations") || l.includes("ops")) return "Operations";
    if (l.includes("finance") || l.includes("accountant")) return "Finance";
    if (l.includes("human resources") || l.includes("hr ") || l === "hr") return "HR";
    return "";
}
